<template>
  <AppNavbar />
  <div class="vx-admin">

    <!-- Encabezado -->
    <div class="d-flex align-items-end justify-content-between mb-3">
      <div>
        <div class="vx-migas mb-1">
          <span>Cobranza</span><span class="sep">/</span><span>Suscripciones</span>
        </div>
        <h1 class="vx-titulo">{{ vocabulario.plural }}</h1>
      </div>
      <div v-if="puedeAdministrar" class="d-flex gap-2">
        <button class="vx-btn" @click="$router.push({ name: 'SuscripcionLote' })">
          <i class="bi bi-people"></i>
          Inscribir varios
        </button>
        <button
          class="vx-btn vx-btn-primario"
          @click="$router.push({ name: 'SuscripcionAlta' })"
        >
          <i class="bi bi-plus-lg"></i>
          Nueva {{ vocabulario.singular.toLowerCase() }}
        </button>
      </div>
    </div>

    <!-- Cifras. Se derivan de lo cargado: no hay endpoint de totales todavía y
         mostrar un número que no podemos sostener sería peor que no mostrarlo. -->
    <div class="row g-2 mb-3">
      <div v-for="c in cifras" :key="c.etiqueta" class="col-6 col-lg-3">
        <div class="vx-panel px-3 py-2">
          <div class="vx-ayuda">{{ c.etiqueta }}</div>
          <div class="vx-num" style="font-size: 24px; font-weight: 650; letter-spacing: -0.02em">
            {{ c.valor }}
          </div>
        </div>
      </div>
    </div>

    <div class="vx-panel">
      <!-- Filtros: barra compacta, no un panel aparte -->
      <div class="d-flex align-items-center gap-2 flex-wrap p-3 border-bottom">
        <div class="position-relative" style="width: 320px; max-width: 100%">
          <i
            class="bi bi-search position-absolute"
            style="left: 10px; top: 50%; transform: translateY(-50%); color: var(--vx-texto-4); font-size: 13px"
          ></i>
          <input
            v-model.trim="filtros.nombre"
            class="vx-campo vx-campo-sm"
            style="padding-left: 30px"
            type="search"
            placeholder="Buscar por nombre o identificador de la ficha"
            aria-label="Buscar suscripciones"
            @keyup.enter="cargar"
          >
        </div>

        <select v-model="filtros.estado" class="vx-campo vx-campo-sm" style="width: auto" aria-label="Estado" @change="cargar">
          <option value="">Todos los estados</option>
          <option value="ACTIVA">Activas</option>
          <option value="SUSPENDIDA">Suspendidas</option>
          <option value="FINALIZADA">Finalizadas</option>
        </select>

        <button class="vx-btn vx-btn-sm" @click="cargar">Aplicar</button>
        <button v-if="hayFiltros" class="vx-btn vx-btn-sm vx-btn-texto" @click="limpiar">Limpiar</button>

        <div class="flex-grow-1"></div>
        <span v-if="!cargando" class="vx-num vx-ayuda">{{ suscripciones.length }} resultados</span>
      </div>

      <!-- Estados de carga, error y vacío: los tres explícitos. Una tabla que
           aparece vacía sin decir por qué es una pantalla rota. -->
      <div v-if="cargando" class="vx-vacio">
        <div class="spinner-border spinner-border-sm text-secondary" role="status"></div>
        <span class="vx-vacio-texto">Cargando…</span>
      </div>

      <div v-else-if="error" class="vx-vacio">
        <i class="bi bi-exclamation-triangle" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">No se pudo cargar el listado</span>
        <span class="vx-vacio-texto">{{ error }}</span>
        <button class="vx-btn vx-btn-sm mt-1" @click="cargar">Reintentar</button>
      </div>

      <div v-else-if="!suscripciones.length" class="vx-vacio">
        <i class="bi bi-people" style="font-size: 22px; color: var(--vx-texto-4)"></i>
        <span class="vx-vacio-titulo">
          {{ hayFiltros ? 'Ningún resultado con esos filtros' : `Todavía no hay ${vocabulario.plural.toLowerCase()}` }}
        </span>
        <span class="vx-vacio-texto">
          {{ hayFiltros
            ? 'Probá con otro nombre o quitá el filtro de estado.'
            : 'Una suscripción define a quién se le cobra, con qué esquema y quién paga.' }}
        </span>
      </div>

      <table v-else class="vx-tabla">
        <thead>
          <tr>
            <th>Ficha</th>
            <th>Esquema</th>
            <th>Responsable de pago</th>
            <th>Estado</th>
            <th style="width: 40px"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="s in suscripciones"
            :key="s.cr_suscripcion_id"
            style="cursor: pointer"
            @click="abrir(s)"
          >
            <td>
              <div class="vx-celda-principal">{{ nombreFicha(s) }}</div>
              <div class="vx-celda-sub vx-num">{{ subtituloFicha(s) }}</div>
            </td>
            <td>{{ s.esquema_nombre }}</td>
            <td>
              <template v-if="s.responsable_nombre">
                <div>{{ s.responsable_nombre }}</div>
              </template>
              <!-- Sin responsable no debería pasar (RN-3 lo impide al crear),
                   pero si pasa hay que verlo, no esconderlo. -->
              <span v-else class="vx-estado vx-estado-atencion">Sin responsable</span>
            </td>
            <td>
              <span class="vx-estado" :class="claseEstado(s.estado)">{{ textoEstado(s.estado) }}</span>
            </td>
            <td class="text-end">
              <i class="bi bi-chevron-right" style="color: var(--vx-texto-4); font-size: 13px"></i>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
// Listado de suscripciones — CR-E1-006.
// SPEC sección 8 · diseño aprobado 2026-08-31.

import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AppNavbar from '@/components/AppNavbar.vue'
import { hasAnyPermission } from '@/services/authSessionService'
import { listarSuscripciones, obtenerVocabulario, VOCABULARIO_POR_DEFECTO } from '@/services/cobranzaService'

const router = useRouter()

const suscripciones = ref([])
const cargando = ref(true)
const error = ref('')
const filtros = reactive({ nombre: '', estado: 'ACTIVA' })

const puedeAdministrar = computed(() => hasAnyPermission(['administrar_suscripciones']))

// Vocabulario configurable (RN-37): el colegio llama "Mensualidad" a lo que el
// core llama "Suscripción". Se lee de `config_parametros_sistema` vía API.
const etiquetas = ref({ ...VOCABULARIO_POR_DEFECTO })
const vocabulario = computed(() => ({
  singular: etiquetas.value['suscripcion.singular'],
  plural: etiquetas.value['suscripcion.plural']
}))

const hayFiltros = computed(() => Boolean(filtros.nombre) || filtros.estado !== 'ACTIVA')

const cifras = computed(() => {
  const cuenta = (e) => suscripciones.value.filter((s) => s.estado === e).length
  const responsables = new Set(
    suscripciones.value.map((s) => s.responsable_cliente_id).filter(Boolean)
  )
  return [
    { etiqueta: 'Activas', valor: cuenta('ACTIVA') },
    { etiqueta: 'Suspendidas', valor: cuenta('SUSPENDIDA') },
    { etiqueta: 'Finalizadas', valor: cuenta('FINALIZADA') },
    { etiqueta: 'Responsables de pago', valor: responsables.size }
  ]
})

// La ficha es opcional (AGENTS.md 0.5): sin ella, la suscripción se identifica
// por su número. Nunca se muestra un hueco en blanco.
const nombreFicha = (s) => s.ficha?.cabecera?.nombre || `Suscripción #${s.cr_suscripcion_id}`

const subtituloFicha = (s) => {
  const cab = s.ficha?.cabecera
  if (!cab) return `#${s.cr_suscripcion_id}`
  const detalle = Object.values(s.ficha?.detalle || {}).filter(Boolean).join(' · ')
  return [cab.descripcion, cab.id, detalle].filter(Boolean).join(' · ')
}

const textoEstado = (e) => ({ ACTIVA: 'Activa', SUSPENDIDA: 'Suspendida', FINALIZADA: 'Finalizada' }[e] || e)
const claseEstado = (e) => (e === 'ACTIVA' ? 'vx-estado-activa' : 'vx-estado-neutro')

const abrir = (s) =>
  router.push({ name: 'SuscripcionDetalle', params: { id: s.cr_suscripcion_id } })

async function cargar () {
  cargando.value = true
  error.value = ''
  try {
    suscripciones.value = await listarSuscripciones({
      estado: filtros.estado,
      nombre: filtros.nombre
    })
  } catch (err) {
    error.value = err?.userMessage || 'Error inesperado al consultar el listado.'
    suscripciones.value = []
  } finally {
    cargando.value = false
  }
}

function limpiar () {
  filtros.nombre = ''
  filtros.estado = 'ACTIVA'
  cargar()
}

onMounted(async () => {
  etiquetas.value = await obtenerVocabulario()
  await cargar()
})
</script>
